import { createContext, useContext } from 'react'
import type { RegisterInput, Session } from '../lib/session'

/* Who is signed in on the storefront.

   Kept out of the provider's own file for the same reason as
   toast-context: a module that exports both a component and a plain value
   breaks fast refresh.

   `status` has three values rather than two because the difference matters
   on the first paint. A page that renders the sign-in form while it is still
   checking a stored token flashes a password box at somebody who is already
   signed in — so 'checking' is a state of its own. */

export type AuthStatus = 'checking' | 'signed-in' | 'signed-out'

export type AccountApi = {
  status: AuthStatus
  session: Session | null
  /** Does this buyer's role allow that? The same question the API asks. */
  can: (permission: string) => boolean
  signIn: (username: string, password: string) => Promise<void>
  register: (input: RegisterInput) => Promise<void>
  signOut: () => void
}

export const AccountContext = createContext<AccountApi | null>(null)

/** The session, from anywhere under <AccountProvider>. */
export function useAccount() {
  const context = useContext(AccountContext)
  if (!context) throw new Error('useAccount must be used inside <AccountProvider>')
  return context
}
